import React, { useContext, useState } from 'react';
import { MdSearch, MdOutlineChatBubbleOutline } from 'react-icons/md';
import { FillAppCommentsContext, type TutorFillAppCommentsCtx } from './commentsContext';
import type { FillAppFieldComment } from 'global/fillapp';
import type { FillAppField as FieldDef, FillAppLabelLink, FillAppLabelBlock, FillAppValues, ChoiceGroups, ChoiceOption } from 'global/fillapp';
import { parseCoursesGrades, CG_SCHEDULE_COLUMNS, CG_SCHEDULE_LABELS, CG_SCALE_LABELS, WORD_LIMITS, WORD_MINIMUMS, wordCount, fieldCharLimit, choiceLabel } from 'global/fillapp';

import './index.css';

// Wraps each help link's text ("Learn more") in a clickable anchor, matching
// the real Common App labels. First case-insensitive occurrence only.
function renderLabel(label: string, links?: FillAppLabelLink[]): React.ReactNode {
    if (!links || links.length === 0) return label;
    let parts: React.ReactNode[] = [label];
    links.forEach((link, li) => {
        let done = false;
        const next: React.ReactNode[] = [];
        for (const part of parts) {
            if (done || typeof part !== 'string') { next.push(part); continue; }
            const idx = part.toLowerCase().indexOf(link.text.toLowerCase());
            if (idx === -1) { next.push(part); continue; }
            done = true;
            if (idx > 0) next.push(part.slice(0, idx));
            next.push(
                <a
                    key={`lnk${li}`}
                    className='ffield__labelLink'
                    href={link.href}
                    target='_blank'
                    rel='noopener noreferrer'
                >
                    {part.slice(idx, idx + link.text.length)}
                </a>,
            );
            if (idx + link.text.length < part.length) next.push(part.slice(idx + link.text.length));
        }
        parts = next;
    });
    return parts;
}

// Labels that carry a bullet list (household independence criteria, fee-waiver
// eligibility) arrive pre-structured as blocks. Rendering the flat string would
// run the bullets together into one paragraph, which is how Common App's own
// markup reads once you strip the <ul>.
function renderLabelBlocks(
    blocks: FillAppLabelBlock[],
    links?: FillAppLabelLink[],
    trailing?: React.ReactNode,
): React.ReactNode {
    // `trailing` (the required asterisk) has to sit INSIDE the last paragraph.
    // Rendered after the blocks it would follow a <p>, which is block-level, so
    // the marker dropped onto a line of its own.
    const lastPara = blocks.map((b, i) => (b.type === 'p' ? i : -1))
        .reduce((acc, i) => (i > acc ? i : acc), -1);
    const out = blocks.map((b, i) => (
        b.type === 'ul' ? (
            <ul key={i} className='ffield__labelList'>
                {b.items.map((item, j) => <li key={j}>{renderLabel(item, links)}</li>)}
            </ul>
        ) : (
            <p key={i} className='ffield__labelPara'>
                {renderLabel(b.text, links)}
                {i === lastPara && trailing}
            </p>
        )
    ));
    // No paragraph to hang it on (a label that is only a list) — give it one.
    return lastPara === -1 && trailing
        ? [...out, <p key='req' className='ffield__labelPara'>{trailing}</p>]
        : out;
}


type Props = {
    field: FieldDef;
    value: unknown;
    values?: FillAppValues;   // full form values (for optionsFilter triggers)
    choiceGroups: ChoiceGroups;
    readOnly?: boolean;
    onChange?: (qid: string, value: unknown) => void;
};

function stripHtml(s: string): string {
    return s.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
}

// Phone values are stored as "<dial> <digits>" (e.g. "+1 5551234567"); empty = ''.
function parsePhone(v: string): { dial: string; number: string } {
    const m = v.match(/^(\+\d{1,4})\s*(.*)$/);
    if (m) return { dial: m[1], number: m[2] };
    return { dial: '+1', number: v };
}

// Address values are stored as an object of subfields; empty = ''.
const ADDRESS_PARTS: { key: string; label: string; narrow?: boolean }[] = [
    { key: 'line1', label: 'Address line 1' },
    { key: 'line2', label: 'Address line 2' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State/Province' },
    { key: 'zip', label: 'ZIP/Postal code', narrow: true },
    { key: 'country', label: 'Country' },
];

function parseAddress(value: unknown): Record<string, string> {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        const out: Record<string, string> = {};
        for (const p of ADDRESS_PARTS) out[p.key] = String((value as Record<string, unknown>)[p.key] ?? '');
        return out;
    }
    return Object.fromEntries(ADDRESS_PARTS.map(p => [p.key, '']));
}

// "Jane Doe" -> "JD" for the comment avatar circle.
function initialsOf(name?: string | null): string {
    const parts = (name || 'Tutor').trim().split(/\s+/).slice(0, 2);
    const out = parts.map(p => (p[0] || '').toUpperCase()).join('');
    return out || 'T';
}

function timeAgo(iso: string): string {
    const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    return `${Math.floor(hr / 24)}d ago`;
}

// Inline per-field comment panel (tutor): list + composer + edit/delete own.
// The open/close toggle lives in FillAppField's label row for consistent placement.
function FieldComments({ qid, value, comments, ctx, choices, onClose }: {
    qid: string;
    value: unknown;
    comments: FillAppFieldComment[];
    ctx: TutorFillAppCommentsCtx;
    choices?: ChoiceOption[];
    onClose: () => void;
}) {
    const [draft, setDraft] = useState('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editDraft, setEditDraft] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const run = (p: Promise<void>, after?: () => void) => {
        setBusy(true);
        setError(null);
        p.then(() => after && after())
            .catch(err => setError(err?.response?.data?.message || 'Something went wrong.'))
            .finally(() => setBusy(false));
    };

    return (
        <div className='ffield__comments'>
            {comments.map(c => (
                <div key={c.id} className={`ffield__comment ${c.isResolved ? 'ffield__comment--resolved' : ''}`}>
                    <div className='ffield__commentMeta'>
                        <span className='ffield__commentAva'>{initialsOf(c.authorName)}</span>
                        <span className='ffield__commentWho'>{c.authorName || 'Tutor'}</span>
                        <span className='ffield__commentActions'>
                            {c.isResolved && <span className='ffield__resolvedChip'>Resolved</span>}
                            {c.authorTutorId != null && c.authorTutorId === ctx.currentTutorId && editingId !== c.id && (
                                <>
                                    {/* A resolved comment is history — no more edits, delete only. */}
                                    {!c.isResolved && (
                                        <button type='button' onClick={() => { setEditingId(c.id); setEditDraft(c.body); }}>Edit</button>
                                    )}
                                    <button type='button' className='ffield__commentDelete' onClick={() => run(ctx.deleteComment(c.id))}>Delete</button>
                                </>
                            )}
                            <span className='ffield__commentWhen'>{timeAgo(c.createdAt)}</span>
                        </span>
                    </div>
                    {editingId === c.id ? (
                        <div className='ffield__commentComposer'>
                            <textarea
                                value={editDraft}
                                maxLength={5000}
                                onChange={e => setEditDraft(e.target.value)}
                            />
                            <div className='ffield__commentComposerRow'>
                                <button type='button' onClick={() => setEditingId(null)} disabled={busy}>Cancel</button>
                                <button
                                    type='button'
                                    className='ffield__commentPrimary'
                                    disabled={busy || !editDraft.trim()}
                                    onClick={() => run(ctx.updateComment(c.id, editDraft.trim()), () => setEditingId(null))}
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    ) : (
                        <p className='ffield__commentBody'>{c.body}</p>
                    )}
                </div>
            ))}
            <div className='ffield__commentComposer'>
                <textarea
                    value={draft}
                    maxLength={5000}
                    placeholder='Leave a comment on this field…'
                    onChange={e => setDraft(e.target.value)}
                />
                <div className='ffield__commentComposerRow'>
                    {error && <span className='ffield__commentError'>{error}</span>}
                    <button type='button' onClick={onClose} disabled={busy}>Close</button>
                    <button
                        type='button'
                        className='ffield__commentPrimary'
                        disabled={busy || !draft.trim()}
                        onClick={() => run(ctx.addComment(qid, draft.trim()), () => setDraft(''))}
                    >
                        Add comment
                    </button>
                </div>
            </div>
        </div>
    );
}

function FillAppField({ field, value, values, choiceGroups, readOnly, onChange }: Props) {
    const commentsCtx = useContext(FillAppCommentsContext);
    const fieldComments = commentsCtx?.commentsByQid[field.qid] || [];
    const hasUnresolved = fieldComments.some(c => !c.isResolved);
    // Panel visibility; the toggle button sits in the label row so its position
    // is identical for every field type. Defaults open when feedback is pending.
    const [commentsOpen, setCommentsOpen] = useState(hasUnresolved);
    const disabled = !!readOnly;
    const allChoices = field.choiceGroup ? choiceGroups[field.choiceGroup] : undefined;
    // Options narrowed by another answer (e.g. course schedule terms by the
    // selected scheduling system). Chips label from the full list regardless.
    let choices = allChoices;
    if (allChoices && field.optionsFilter && values) {
        // Lowercase keys vs capitalized stored codes ("Semester") — normalize;
        // unanswered trigger allows nothing (mirrors the student form).
        const key = String(values[field.optionsFilter.qid] ?? '').toLowerCase();
        const allowed = field.optionsFilter.map[key] ?? [];
        choices = allChoices.filter(c => allowed.includes(String(c.exportCode)));
    }
    const v = value == null ? '' : String(value);

    const handleChange = (next: unknown) => {
        if (!disabled && onChange) onChange(field.qid, next);
    };

    const rawLabel = stripHtml(field.label || '');
    // The xlsx labels often include a trailing " *" for required; strip it so
    // we can re-render the asterisk consistently from the required flag.
    const label = rawLabel.replace(/\s*\*\s*$/, '');
    const isNarrow = field.type === 'Date' || field.type === 'Month' || field.qid === 'q_176';

    // Helper text harvested from the Common App captures (control__hint).
    const hintNode = field.hint
        ? <div className='ffield__hintText'>{field.hint}</div>
        : null;

    // Common App limits: mirror the student-side counter so tutors can see
    // when an answer runs over its word/character cap.
    const isTextEntry = field.type === 'Short' || field.type === 'Text'
        || field.type === 'Email' || field.type === 'Long';
    const wordLimit = isTextEntry ? WORD_LIMITS[field.qid] : undefined;
    const wordMin = isTextEntry ? WORD_MINIMUMS[field.qid] : undefined;
    const charLimit = isTextEntry && !wordLimit ? fieldCharLimit(field) : null;
    const words = wordLimit ? wordCount(v) : 0;
    const isOver = wordLimit ? words > wordLimit : charLimit != null && v.length > charLimit;
    const overClass = isOver ? 'ffield__input--over' : '';
    const counterNode = (!!wordLimit || charLimit != null) && v.length > 0
        ? (
            <div className={`ffield__count ${isOver ? 'ffield__count--over' : ''}`}>
                {wordLimit
                    ? `${words} / ${wordLimit} words`
                    : `${v.length} / ${charLimit} characters`}
                {isOver && ' — over limit'}
                {!isOver && wordMin != null && words > 0 && words < wordMin && ' — under the minimum'}
            </div>
        )
        : null;
    // Min/max left-aligned under the box, counter on the right (student parity).
    const limitsNode = wordMin
        ? (
            <div className='ffield__limitRow'>
                <span className='ffield__minMax'>{`Min: ${wordMin} / Max: ${wordLimit}`}</span>
                {counterNode}
            </div>
        )
        : counterNode;

    let control: React.ReactNode;
    switch (field.type) {
        case 'Short':
        case 'Text':
        case 'Email':
            control = (
                <input
                    className={`ffield__input ${isNarrow ? 'ffield__input--narrow' : ''} ${overClass}`}
                    type={field.type === 'Email' ? 'email' : 'text'}
                    value={v}
                    disabled={disabled}
                    onChange={e => handleChange(e.target.value)}
                />
            );
            break;
        case 'Phone': {
            // Read-only mirror of the student dial-code + number layout.
            const phone = parsePhone(v);
            control = (
                <div className='ffield__phone'>
                    <input
                        className='ffield__input ffield__phoneCode'
                        type='text'
                        value={v === '' ? '' : phone.dial}
                        disabled={disabled}
                        aria-label='Country code'
                        onChange={() => undefined}
                    />
                    <input
                        className='ffield__input ffield__phoneNumber'
                        type='tel'
                        value={phone.number}
                        disabled={disabled}
                        aria-label='Phone number'
                        onChange={() => undefined}
                    />
                </div>
            );
            break;
        }
        case 'Upload':
            control = (
                <input
                    className='ffield__input'
                    type='text'
                    value={v}
                    placeholder='No document uploaded'
                    disabled={disabled}
                    onChange={() => undefined}
                />
            );
            break;
        case 'CoursesGrades': {
            // Read-only transcript tables (the student edits via a dialog).
            const cg = parseCoursesGrades(value);
            control = !cg || cg.transcripts.length === 0 ? (
                <div className='ffield__cgEmpty'>No courses entered</div>
            ) : (
                <div className='ffield__cg'>
                    {cg.transcripts.map((t, ti) => {
                        const cols = CG_SCHEDULE_COLUMNS[t.schedule] ?? [];
                        return (
                            <div key={ti} className='ffield__cgTranscript'>
                                <div className='ffield__cgHead'>
                                    <b>{t.school || `Transcript #${ti + 1}`}</b>
                                    {[t.year,
                                        CG_SCALE_LABELS[t.scale] || t.scale,
                                        t.schedule === 'other'
                                            ? (t.scheduleOther || 'Other')
                                            : (CG_SCHEDULE_LABELS[t.schedule] || t.schedule)]
                                        .filter(Boolean).map((m, i) => (
                                            <span key={i} className='ffield__cgMeta'>{String(m)}</span>
                                        ))}
                                </div>
                                {/* A trimester transcript is 9+ columns; the review rail is
                                    too narrow for that, so the table scrolls sideways inside
                                    its transcript card instead of crushing the columns. */}
                                <div className='ffield__cgScroll'>
                                    <table className='ffield__cgTable'>
                                        <thead>
                                            <tr>
                                                <th>Subject</th>
                                                <th>Course Name</th>
                                                <th>Level</th>
                                                {cols.map(c => <th key={`g${c}`}>Grade {c}</th>)}
                                                {cols.map(c => <th key={`c${c}`}>Credit {c}</th>)}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {t.courses.map((c, ci) => (
                                                <tr key={ci}>
                                                    <td>{c.subject}</td>
                                                    <td>{c.name}</td>
                                                    <td>{c.level}</td>
                                                    {cols.map(col => <td key={`g${col}`}>{c.grades[col] || ''}</td>)}
                                                    {cols.map(col => (
                                                        <td key={`c${col}`}>{c.creditsNA ? 'N/A' : (c.credits[col] || '')}</td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
            break;
        }
        case 'Address': {
            const addr = parseAddress(value);
            control = (
                <div className='ffield__address'>
                    {ADDRESS_PARTS.map(p => (
                        <label key={p.key} className={`ffield__addressPart ${p.narrow ? 'ffield__addressPart--narrow' : ''}`}>
                            <span className='ffield__addressLabel'>{p.label}</span>
                            <input
                                className='ffield__input'
                                type='text'
                                value={addr[p.key]}
                                disabled={disabled}
                                onChange={() => undefined}
                            />
                        </label>
                    ))}
                </div>
            );
            break;
        }
        case 'Long':
            control = (
                <textarea
                    className={`ffield__textarea ${overClass}`}
                    value={v}
                    disabled={disabled}
                    onChange={e => handleChange(e.target.value)}
                />
            );
            break;
        case 'Date':
            control = (
                <input
                    className='ffield__input ffield__input--narrow'
                    type='date'
                    value={v}
                    disabled={disabled}
                    onChange={e => handleChange(e.target.value)}
                />
            );
            break;
        case 'Month':
            control = (
                <input
                    className='ffield__input ffield__input--narrow'
                    type='month'
                    value={v}
                    disabled={disabled}
                    onChange={e => handleChange(e.target.value)}
                />
            );
            break;
        case 'SingleSelectDropdown':
            control = (
                <select
                    className={`ffield__select ${isNarrow ? 'ffield__select--narrow' : ''}`}
                    value={v}
                    disabled={disabled}
                    onChange={e => handleChange(e.target.value)}
                >
                    <option value=''>— Select —</option>
                    {(choices || []).map((c, i) => (
                        <option key={i} value={c.exportCode}>{c.label}</option>
                    ))}
                </select>
            );
            break;
        case 'CollegeLookup':
        case 'SchoolLookup':
            // Free-text institution name (students type it; no lookup DB).
            control = (
                <div className='ffield__lookup'>
                    <MdSearch className='ffield__lookupIcon' />
                    <input
                        className='ffield__input ffield__lookupInput'
                        type='text'
                        value={v}
                        disabled={disabled}
                        onChange={e => handleChange(e.target.value)}
                    />
                </div>
            );
            break;
        case 'MultiSelectDropdown': {
            // Read-only chips of the selected options (ng-select style).
            const arrVal = Array.isArray(value) ? (value as unknown[]).map(String) : (v ? v.split(',') : []);
            const labelOf = (code: string) =>
                (allChoices || []).find(c => String(c.exportCode) === code)?.label ?? code;
            control = (
                <div className='ffield__mselControl ffield__mselControl--disabled'>
                    {arrVal.length === 0 ? (
                        <span className='ffield__mselPlaceholder'>- Choose one or more options -</span>
                    ) : (
                        <span className='ffield__mselChips'>
                            {arrVal.map(code => (
                                <span key={code} className='ffield__mselChip'>{labelOf(code)}</span>
                            ))}
                        </span>
                    )}
                </div>
            );
            break;
        }
        case 'Radio':
            control = (
                <div className='ffield__radioGroup'>
                    {(choices || []).map((c, i) => {
                        const selected = v === String(c.exportCode);
                        return (
                            <label
                                key={i}
                                className={`ffield__radioOption ${selected ? 'ffield__radioOption--selected' : ''} ${disabled ? 'disabled' : ''}`}
                            >
                                <input
                                    className='ffield__radioInput'
                                    type='radio'
                                    name={field.qid}
                                    value={c.exportCode}
                                    checked={selected}
                                    disabled={disabled}
                                    onChange={() => handleChange(c.exportCode)}
                                />
                                <span className='ffield__radioDot' />
                                <span>{c.label}</span>
                            </label>
                        );
                    })}
                </div>
            );
            break;
        case 'Checkbox': {
            const arrVal = Array.isArray(value) ? (value as string[]) : (v ? v.split(',') : []);
            control = (
                <div className='ffield__checkboxGroup'>
                    {(choices || []).map((c, i) => {
                        const checked = arrVal.includes(String(c.exportCode));
                        return (
                            <label key={i} className='ffield__checkboxOption'>
                                <input
                                    type='checkbox'
                                    checked={checked}
                                    disabled={disabled}
                                    onChange={() => {
                                        const next = checked
                                            ? arrVal.filter(x => x !== String(c.exportCode))
                                            : [...arrVal, String(c.exportCode)];
                                        handleChange(next);
                                    }}
                                />
                                <span>{c.label}</span>
                            </label>
                        );
                    })}
                </div>
            );
            break;
        }
        default:
            control = (
                <input
                    className='ffield__input'
                    type='text'
                    value={v}
                    disabled={disabled}
                    placeholder={field.type ? `(${field.type})` : ''}
                    onChange={e => handleChange(e.target.value)}
                />
            );
    }

    // Display-only text rows (ca-label): no input, no required marker.
    if (field.type === 'Label') {
        return (
            <div className='ffield'>
                <div className='ffield__labelText'>
                    {field.labelBlocks
                        ? renderLabelBlocks(field.labelBlocks, field.labelLinks)
                        : <p>{renderLabel(label, field.labelLinks)}</p>}
                </div>
            </div>
        );
    }

    return (
        <div className='ffield'>
            {field.boldLead?.block && (
                <div className='ffield__subheader'>{field.boldLead.text}</div>
            )}
            <div className='ffield__label'>
                <span>
                    {field.boldLead && !field.boldLead.block && (
                        <b>{field.boldLead.text}&nbsp;</b>
                    )}
                    {field.labelBlocks
                        ? renderLabelBlocks(
                            field.labelBlocks, field.labelLinks,
                            field.required
                                ? <span className='ffield__required'>&nbsp;*</span>
                                : undefined)
                        : <>
                            {renderLabel(label, field.labelLinks)}
                            {field.required && <span className='ffield__required'>&nbsp;*</span>}
                        </>}
                </span>
                {commentsCtx && (
                    <button
                        type='button'
                        className={`ffield__commentBtn ${fieldComments.length > 0 ? (hasUnresolved ? 'ffield__commentBtn--open' : 'ffield__commentBtn--done') : ''}`}
                        onClick={() => setCommentsOpen(o => !o)}
                    >
                        <MdOutlineChatBubbleOutline size={13} />
                        {/* Count OPEN notes only, matching the card/sidebar badges;
                            all-resolved shows the green chip; empty invites a comment. */}
                        {fieldComments.length === 0
                            ? 'Comment'
                            : hasUnresolved
                                ? fieldComments.filter(c => !c.isResolved).length
                                : 'Resolved'}
                    </button>
                )}
            </div>
            {control}
            {field.sensitive && (
                <div className='ffield__privacyNote'>
                    Not collected in College Apps for privacy — students complete this on the real
                    Common App.
                </div>
            )}
            {/* Hint directly under the input, before the counter (Common App
                mat-hint placement). */}
            {hintNode}
            {limitsNode}
            {commentsCtx && commentsOpen && (
                <FieldComments
                    qid={field.qid}
                    value={value}
                    comments={fieldComments}
                    ctx={commentsCtx}
                    choices={allChoices}
                    onClose={() => setCommentsOpen(false)}
                />
            )}
        </div>
    );
}

export default FillAppField;
